
import type { OrgSubscriptionPermissions } from 'src/api/subscriptionsApi';
import { mapClientPermissionToResellerRole } from 'src/constants/constants';
import { resolveRegistryParentModulePermission } from 'src/mindtime/config/subscriptionPermissionRegistry';
import {
  hasExplicitPermission,
  isRoleOnlyEdrPermission,
  mapResellerRoleKeyToCatalogKey,
} from 'src/mindtime/utils/permissionKeys';

export {
  expandRolePermissionKeys,
  hasExplicitPermission,
  isRoleOnlyEdrPermission,
  mapResellerRoleKeyToCatalogKey,
  normalizeRegistryPermissionKey,
  toPermissionKeySet,
} from 'src/mindtime/utils/permissionKeys';

const READ_SUFFIX = '-read';
const CREATE_SUFFIX = '-create';

/**
 * Subscription access matrix (single resolver + canAccess):
 *
 * | Tier        | effectiveKeys   | READ module/widget        | `-create` (deploy)     |
 * |-------------|-----------------|---------------------------|-------------------------|
 * | reseller    | JWT role        | root module `-read` first; each widget needs explicit component `-read` | explicit role only      |
 * | preview     | JWT role        | root module `-read` first; each widget needs explicit component `-read` | UI: role; route: block  |
 * | subscribed  | org subscription first, then JWT role restrictor | subscription AND role | explicit in subscription AND role |
 * | EDR Clients | JWT role always  | role-only (all tiers)     | n/a                     |
 */

export type SubscriptionAccessTier = 'reseller' | 'preview' | 'subscribed';

export type SubscriptionAccessContext = {
  tier: SubscriptionAccessTier;
  effectiveKeys: ReadonlySet<string>;
  isReseller: boolean;
  clientSubscribed: boolean;
  roleKeys: ReadonlySet<string>;
  subscriptionKeys: ReadonlySet<string>;
  checkRole?: (permissions: string[]) => boolean;
};

export type CanAccessOptions = {
  parentPermission?: string;
  /** Block `-create` routes for preview clients (deploy page). UI checks omit this. */
  blockPreviewCreate?: boolean;
};

/** Case-insensitive Reseller org type check. */
export const resolveIsResellerOrg = (organizationTypeName?: string | null): boolean =>
  String(organizationTypeName ?? '').trim().toLowerCase() === 'reseller';

/** Client org with loaded entitlements and an active subscription. */
export const resolveClientSubscribedAccess = (
  isReseller: boolean,
  orgEntitlements: OrgSubscriptionPermissions | null,
  hasActiveSubscription: boolean,
): boolean => !isReseller && orgEntitlements !== null && hasActiveSubscription;

/** Reseller uses JWT immediately; client orgs wait for subscription API (avoid preview flash). */
export const resolveEdrPermissionsReady = (params: {
  isReseller: boolean;
  authInitialized: boolean;
  hasAuthUser: boolean;
  subscriptionResolved: boolean;
}): boolean => {
  if (!params.authInitialized || !params.hasAuthUser) return false;
  // Resellers never use org subscription keys — ready once auth is available.
  if (params.isReseller) return true;
  // Client orgs: wait until entitlements load. Otherwise tier looks like "preview"
  // (orgEntitlements still null) and role keys briefly show modules that subscription will deny.
  return params.subscriptionResolved;
};

export const resolveSubscriptionAccessContext = (params: {
  isReseller: boolean;
  clientSubscribed: boolean;
  roleKeys: ReadonlySet<string>;
  subscriptionKeys: ReadonlySet<string>;
  checkRole?: (permissions: string[]) => boolean;
}): SubscriptionAccessContext => {
  const tier: SubscriptionAccessTier = params.isReseller
    ? 'reseller'
    : params.clientSubscribed
      ? 'subscribed'
      : 'preview';

  return {
    tier,
    effectiveKeys: tier === 'subscribed' ? params.subscriptionKeys : params.roleKeys,
    isReseller: params.isReseller,
    clientSubscribed: params.clientSubscribed,
    roleKeys: params.roleKeys,
    subscriptionKeys: params.subscriptionKeys,
    checkRole: params.checkRole,
  };
};

export const buildSubscriptionAccessContext = (params: {
  isReseller?: boolean;
  organizationTypeName?: string | null;
  orgEntitlements: OrgSubscriptionPermissions | null;
  hasActiveSubscription: boolean;
  roleKeys: ReadonlySet<string>;
  subscriptionKeys: ReadonlySet<string>;
  checkRole?: (permissions: string[]) => boolean;
  /** When false, subscribed clients fall back to JWT role (non-registry permissions). */
  useSubscriptionEntitlements?: boolean;
}): SubscriptionAccessContext => {
  const isResellerOrg =
    resolveIsResellerOrg(params.organizationTypeName) || Boolean(params.isReseller);

  const clientSubscribed = isResellerOrg
    ? false
    : resolveClientSubscribedAccess(
        false,
        params.orgEntitlements,
        params.hasActiveSubscription,
      );

  return resolveSubscriptionAccessContext({
    isReseller: isResellerOrg,
    clientSubscribed: clientSubscribed && (params.useSubscriptionEntitlements ?? true),
    roleKeys: params.roleKeys,
    subscriptionKeys: params.subscriptionKeys,
    checkRole: params.checkRole,
  });
};

export const hasAnyRolePermission = (
  checkRole: (permissions: string[]) => boolean,
  permission: string,
): boolean => {
  const normalized = String(permission ?? '').trim().toLowerCase();
  const candidates = new Set<string>([normalized]);
  candidates.add(mapClientPermissionToResellerRole(normalized));
  const catalogKey = mapResellerRoleKeyToCatalogKey(normalized);
  if (catalogKey) candidates.add(catalogKey);
  return [...candidates].some((candidate) => checkRole([candidate]));
};

const permissionBody = (identifier: string): string => {
  if (identifier.endsWith(READ_SUFFIX)) {
    return identifier.slice(0, -READ_SUFFIX.length);
  }
  if (identifier.endsWith(CREATE_SUFFIX)) {
    return identifier.slice(0, -CREATE_SUFFIX.length);
  }
  return identifier;
};

/**
 * Role / full-module check: explicit component key, or parent module key grants all children.
 */
export const hasSubscriptionPermission = (
  keys: ReadonlySet<string>,
  componentPermission: string,
  parentPermission: string,
): boolean => {
  if (hasExplicitPermission(keys, componentPermission)) return true;

  return hasExplicitPermission(keys, parentPermission);
};

export const hasGranularModuleChildEntitlements = (
  keys: ReadonlySet<string>,
  parentPermission: string,
): boolean => {
  const moduleBody = permissionBody(parentPermission);

  for (const key of keys) {
    if (!key.endsWith(READ_SUFFIX)) continue;
    const body = permissionBody(key);
    if (body.startsWith(`${moduleBody}_`)) {
      return true;
    }
  }

  return false;
};

/** Subscribed widget: explicit child, or parent `-read` when subscription is full-module only. */
const hasGranularComponentEntitlement = (
  keys: ReadonlySet<string>,
  componentPermission: string,
  parentPermission: string,
): boolean => {
  if (hasExplicitPermission(keys, componentPermission)) return true;

  if (!hasExplicitPermission(keys, parentPermission)) return false;

  return !hasGranularModuleChildEntitlements(keys, parentPermission);
};

/** Nav/page access: module permission or any child under that module. */
export const hasSubscriptionModulePermission = (
  keys: ReadonlySet<string>,
  modulePermissionKey: string,
): boolean => {
  if (hasExplicitPermission(keys, modulePermissionKey)) return true;
  const moduleBody = permissionBody(String(modulePermissionKey ?? '').trim().toLowerCase());
  for (const key of keys) {
    const body = permissionBody(key);
    if (body.startsWith(`${moduleBody}_`)) return true;
  }
  return false;
};

/** Module or component permission (parent module grants child routes/components). */
export const hasRegistryPermissionAccess = (
  keys: ReadonlySet<string>,
  permission: string,
): boolean => {
  if (hasSubscriptionModulePermission(keys, permission)) return true;

  const parent = resolveRegistryParentModulePermission(permission);
  if (!parent) return false;

  if (permission.endsWith(CREATE_SUFFIX)) {
    return hasExplicitPermission(keys, permission);
  }

  return hasSubscriptionPermission(keys, permission, parent);
};

const evaluateRoleTierAccess = (
  context: SubscriptionAccessContext,
  permission: string,
  parentPermission?: string,
): boolean => {
  if (permission.endsWith(CREATE_SUFFIX)) {
    return (
      hasExplicitPermission(context.effectiveKeys, permission) ||
      (context.checkRole ? hasAnyRolePermission(context.checkRole, permission) : false)
    );
  }

  if (parentPermission) {
    // Reseller/preview: root module `-read` first, then explicit component `-read`.
    const parentGranted =
      hasExplicitPermission(context.effectiveKeys, parentPermission) ||
      (context.checkRole ? hasAnyRolePermission(context.checkRole, parentPermission) : false);
    if (!parentGranted) return false;

    if (hasExplicitPermission(context.effectiveKeys, permission)) return true;
    return context.checkRole ? hasAnyRolePermission(context.checkRole, permission) : false;
  }

  // Module / route / nav: require explicit root module `-read` (do not infer from children).
  return (
    hasExplicitPermission(context.effectiveKeys, permission) ||
    (context.checkRole ? hasAnyRolePermission(context.checkRole, permission) : false)
  );
};

const evaluateSubscribedTierAccess = (
  keys: ReadonlySet<string>,
  permission: string,
  parentPermission?: string,
): boolean => {
  if (permission.endsWith(CREATE_SUFFIX)) {
    return hasExplicitPermission(keys, permission);
  }

  if (parentPermission) {
    return hasGranularComponentEntitlement(keys, permission, parentPermission);
  }

  return hasRegistryPermissionAccess(keys, permission);
};

/** Org subscription entitlement only (ignores JWT role). */
export const hasSubscriptionEntitlement = (
  context: SubscriptionAccessContext,
  permission: string,
  parentPermission?: string,
): boolean =>
  evaluateSubscribedTierAccess(context.subscriptionKeys, permission, parentPermission);

/** Single permission check — routes, nav, and page widgets. */
export const canAccess = (
  context: SubscriptionAccessContext,
  permission: string,
  options: CanAccessOptions = {},
): boolean => {
  const { parentPermission, blockPreviewCreate = false } = options;

  if (blockPreviewCreate && context.tier === 'preview' && permission.endsWith(CREATE_SUFFIX)) {
    return false;
  }

  // EDR Clients: always JWT role (including subscribed clients) — not org subscription keys.
  if (
    isRoleOnlyEdrPermission(permission) ||
    (parentPermission ? isRoleOnlyEdrPermission(parentPermission) : false)
  ) {
    return evaluateRoleTierAccess(
      {
        ...context,
        tier: context.isReseller ? 'reseller' : 'preview',
        effectiveKeys: context.roleKeys,
      },
      permission,
      parentPermission,
    );
  }

  if (context.tier === 'subscribed') {
    // Subscription first (priority): org must be entitled.
    if (!evaluateSubscribedTierAccess(context.subscriptionKeys, permission, parentPermission)) {
      return false;
    }
    // Role behind subscription: user role may only narrow access further.
    return evaluateRoleTierAccess(
      {
        ...context,
        effectiveKeys: context.roleKeys,
      },
      permission,
      parentPermission,
    );
  }

  return evaluateRoleTierAccess(context, permission, parentPermission);
};
